import { DEFAULT_PAGE_SIZE } from '@/constants';
import { EnableStatus, ListResponse } from '@/types/common';
import {
  ActivationMode,
  MemberCardExtendRecord,
  MemberCardList,
  MemberCardStatus,
  MemberImportCheckResult,
  MemberImportConfirmResult,
  MemberImportDetailResult,
  MemberImportFileResult,
  MemberImportFileType,
  MemberInfo,
  MemberStatistic,
  MemberStatus,
  SearchUserInfo,
  UserMemberCardActionType,
  UserMemberCardInfo,
  UserStageType,
} from '@/types/member';
import { PerformanceOwnershipItem } from '@/types/staff';
import { cleanObject } from '@/utils';
import { requestWrapper } from '@/utils/request';

export type CreateUserMemberCardParams = {
  id: number;
  user_id: number; // 用户 ID，关联用户表的主键，一个用户可有多条开卡记录
  activate_type: ActivationMode; // 激活方式：1:立即激活 2:约课, 或者到期自动激活
  member_card_id: number; // 会员卡 ID，关联会员卡表的 card_id
  coupon_issue_id?: number; // 优惠券ID
  pay_image: string; // 付款凭证图片的名称
  auto_activate_days: number; // 自动激活的天数
  actual_amount: number; // 实收金额，单位为分
  issuing_store_id: number; // 开卡门店的 ID
  issuing_admin_id: number; // 开卡的负责人 ID
  issuing_remarks: string; // 开卡的备注信息
  stop_days: number; // 停卡时长
  return_amount: number; // 退卡金额
  exp_time: string; // 新有效期至
  action_remark: string; // 操作备注
  action_type: UserMemberCardActionType; // 操作类型
  is_return: boolean; // 是否退钱
  reason: string; // 停卡原因
  source_type: string; // 来源类型
  valid_days: number; // 有效期(天数)
  total_card_num: number; // 自定义开卡次数
  activate_time: string; // 新激活日期
  allocation: PerformanceOwnershipItem[]; // 业绩归属信息
  class_id?: number; // 开卡后加入的班级 ID（选填）
};

export type VoidUserMemberCardParams = {
  id: number;
  reason: string;
  refund_order: boolean;
};

export type VoidUserMemberCardResult = {
  id: number;
  order_no: string;
  is_refund: boolean;
  refund_amount: number;
};

// AST Filter 字段白名单
type FilterField =
  | 'user_type' // 用户类型（int）
  | 'admin_id' // 负责人ID（int）
  | 'intention_type' // 意向类型（int）
  | 'total_used_class_num' // 累计上课节数（float）
  | 'remain_card_num' // 剩余课次（float）
  | 'total_card_num' // 总课次（float）
  | 'first_card_time' // 首次开卡时间（datetime）
  | 'latest_study_time' // 最近上课时间（datetime）
  | 'latest_manual_follow_time' // 最近人工跟进时间（datetime）
  | 'card_exp_time' // 会员卡到期时间（datetime）
  | 'next_follow_time' // 下次跟进时间（datetime）
  | 'phone_number' // 手机号（string）
  | 'name' // 姓名（string）
  | 'remarks'; // 备注（string）

// AST Filter 类型定义（递归结构）
export type FilterRule = {
  field?: FilterField;
  op: 'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte' | 'and' | 'or';
  value?: string | number;
  rules?: FilterRule[];
};

// 请求会员列表的参数
export type GetMemberListParams = {
  page: number;
  page_size: number;
  is_member: MemberStatus;
  store_id: number;
  sort_asc?: boolean;
  sort_by?: string;
  filter?: FilterRule;
};

// 请求会员开卡列表的参数
export type GetMemberCardListParams = {
  // 门店id
  store_id: number;
  // 会员卡id
  member_card_id: number;
  member_card_ids: number[];
  statuses: MemberCardStatus[];
  // 管理员id
  admin_id: number;
  page: number; // 当前页码，默认为 1
  page_size: number; // 每页显示的记录数
  // 开卡时间范围
  card_issuing_start_time: string; // 开卡时间的起始时间 (YYYY-MM-DD)
  card_issuing_end_time: string; // 开卡时间的结束时间 (YYYY-MM-DD)
  include_void?: EnableStatus; // 是否作废
};

// 获取会员详情
export async function getMemberInfo(uid: number) {
  return requestWrapper<MemberInfo>('/api/user_member/info', {
    method: 'GET',
    params: { user_id: uid },
  });
}

// 更新会员基本信息
export async function updateMemberInfo(body: Partial<MemberInfo>) {
  return requestWrapper<MemberInfo>('/api/user_member/update', {
    method: 'POST',
    data: body,
  });
}

// 会员开卡
export async function createUserMemberCard(body: Partial<CreateUserMemberCardParams>) {
  return requestWrapper<{ id: number }>('/api/user_member_card/create', {
    method: 'POST',
    data: cleanObject(body),
  });
}

// 更新会员卡
export async function updateUserMemberCard(body: Partial<CreateUserMemberCardParams>) {
  return requestWrapper<{ id: number }>('/api/user_member_card/update', {
    method: 'POST',
    data: cleanObject(body),
  });
}

// 作废会员卡
export async function voidUserMemberCard(body: VoidUserMemberCardParams) {
  return requestWrapper<VoidUserMemberCardResult>('/api/user_member_card/void', {
    method: 'POST',
    data: cleanObject(body),
  });
}

// 获取用户拥有的会员卡列表
export async function getUserMemberCardList(params: { user_id: number }) {
  return requestWrapper<UserMemberCardInfo[]>('/api/user_member_card/list', {
    method: 'GET',
    params,
  });
}

// 获取会员数量统计
export async function getMemberStatistic(params: {
  store_id: number; // 门店 ID
  is_member: MemberStatus; // 是否是会员，0 - 否，1有效会员，2无效会员
}) {
  return requestWrapper<MemberStatistic>('/api/user_member_card/statistic', {
    method: 'GET',
    params,
  });
}

// 获取会员列表（支持 AST 过滤）
export async function getMemberList(data: Partial<GetMemberListParams> = {}) {
  const defaultData = {
    page: 1,
    page_size: DEFAULT_PAGE_SIZE,
    is_member: MemberStatus.VALID,
  };

  const mergedData = {
    ...defaultData,
    ...data,
  };

  return requestWrapper<ListResponse<MemberInfo>>('/api/user_member/list_v2', {
    method: 'POST',
    data: mergedData,
  });
}

// 通过手机号搜索用户/线索
export async function queryUserLeads(params: {
  search_types?: UserStageType[]; // 搜索类型，1 表示用户，2 表示线索
  keywords: string; // 搜索关键词
}) {
  return requestWrapper<{
    user: SearchUserInfo[];
    leads: SearchUserInfo[];
  }>('/api/common/search', {
    method: 'GET',
    params,
  });
}

//会员开卡列表
export async function getMemberCardListData(params: Partial<GetMemberCardListParams> = {}) {
  return requestWrapper<ListResponse<MemberCardList>>('/api/user_member_card/list_page', {
    method: 'GET',
    params,
  });
}

// 导出会员开卡记录
export async function exportMemberCardList(params: Partial<GetMemberCardListParams> = {}) {
  return requestWrapper<{ url: string }>('/api/user_member_card/export', {
    method: 'GET',
    params,
  });
}

// 转卡
export async function transferMemberCard(data: {
  to_user_id: number;
  user_member_card_id: number;
}) {
  return requestWrapper<any>('/api/user_member_card/change', {
    method: 'POST',
    data,
  });
}

// 换绑手机号
export async function changeMemberPhone(data: { user_id: number; phone_number: string }) {
  return requestWrapper<any>('/api/user/change_mobile', {
    method: 'POST',
    data,
  });
}

// 添加子账号
export async function addSubAccount(data: {
  child_name: string;
  status: number;
  user_type: number;
  gender: number;
  master_user_id: number;
}) {
  return requestWrapper<any>('/api/user_subaccount_relation/create', {
    method: 'POST',
    data,
  });
}

// 批量延期会员卡
export async function batchExtendMemberCard(data: {
  store_id: number; // 门店ID，0表示全部门店
  member_card_ids?: number[]; // 会员卡ID列表，不传表示全部会员卡
  extend_days: number; // 延期天数
  reason: string; // 延期原因
}) {
  return requestWrapper('/api/user_member_card/batch_extend', {
    method: 'POST',
    data,
  });
}

// 获取批量延期操作记录
export async function getExtendRecordList(params: { page: number; page_size: number }) {
  return requestWrapper<ListResponse<MemberCardExtendRecord>>(
    '/api/user_member_card/batch_extend_log/list_page',
    {
      method: 'GET',
      params,
    },
  );
}

// 退出用户登录
export async function logoutUser(params: { user_id: number }) {
  return requestWrapper('/api/user/wx_logout', {
    method: 'POST',
    data: params,
  });
}

/** 上传会员导入 Excel 并检测 */
export async function checkMemberImportFile(
  file: File,
  params?: { merchant_id?: number; auto_activate_days?: number },
) {
  const formData = new FormData();
  formData.append('file', file);
  if (params?.merchant_id !== undefined) {
    formData.append('merchant_id', String(params.merchant_id));
  }
  if (params?.auto_activate_days !== undefined) {
    formData.append('auto_activate_days', String(params.auto_activate_days));
  }

  return requestWrapper<MemberImportCheckResult>('/api/user_member_card/import/check', {
    method: 'POST',
    data: formData,
  });
}

/** 确认执行会员导入 */
export async function confirmMemberImport(batchNo: string) {
  return requestWrapper<MemberImportConfirmResult>('/api/user_member_card/import/confirm', {
    method: 'POST',
    data: { batch_no: batchNo },
  });
}

/** 查询导入批次详情 */
export async function getMemberImportDetail(batchNo: string) {
  return requestWrapper<MemberImportDetailResult>('/api/user_member_card/import/detail', {
    method: 'GET',
    params: { batch_no: batchNo },
  });
}

/** 获取导入相关文件下载链接 */
export async function getMemberImportFile(params: {
  batch_no: string;
  file_type: MemberImportFileType;
}) {
  return requestWrapper<MemberImportFileResult>('/api/user_member_card/import/file', {
    method: 'GET',
    params,
  });
}
